import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonAddDisabledIcon from '@mui/icons-material/PersonAddDisabled';
import {
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSWRConfig } from 'swr';
import AxiosConfig from '../../../config/AxiosConfig';
import LoadingButton from '../utils/common/LoadingButton.jsx';

const ManageUser = ({ user, setSelectedUser, setDisplayContent }) => {
  const roles = useSelector((state) => state?.static?.roles);
  const permissions = useSelector((state) => state?.static?.permissions);
  const { mutate: mutateUsers } = useSWRConfig();
  const [isActive, setIsActive] = useState(true);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [sipPassword, setSipPassword] = useState('');
  const [sipExtension, setSipExtension] = useState('');
  const [newRoles, setNewRoles] = useState([]);
  const [permission, setPermission] = useState(permissions[0]?._id || null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setIsActive(user?.isActive);
      setEmail(user?.email);
      setFirstName(user?.firstName);
      setLastName(user?.lastName);
      setNewRoles(user?.roles?.map((r) => r?._id));
      setPermission(user?.permission?._id);
      setSipPassword(user?.sipPassword);
      setSipExtension(user?.sipExtension);
    }
  }, [user]);

  const emailRegex = new RegExp(
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  );

  const handleIsActiveChange = (_event) => {
    setIsActive((state) => !state);
  };

  const handleEmailChange = (event) => {
    setEmail(event.target.value?.trim()?.toLowerCase());
  };

  const handleFirstNameChange = (event) => {
    setFirstName(event.target.value);
  };

  const handleLastNameChange = (event) => {
    setLastName(event.target.value);
  };

  const handleSipPasswordChange = (event) => {
    setSipPassword(event.target.value);
  };

  const handleSipExtensionChange = (event) => {
    setSipExtension(event.target.value);
  };

  const handleRolesChange = (roleId) => {
    if (newRoles?.includes(roleId)) {
      setNewRoles((r) => [...r].filter((e) => e !== roleId));
    } else {
      setNewRoles((r) => [...r, roleId]);
    }
  };

  const handleChangePermission = (event) => {
    setPermission(event.target.value);
  };

  const handleCancelClick = (_event) => {
    setSelectedUser(null);
    setDisplayContent('ElementList');
  };

  const handleConfirmClick = (_event) => {
    if (!email?.match(emailRegex)) {
      setEmailError('Email invalide.');
    } else {
      setIsLoading(true);
      setEmailError(null);
      AxiosConfig.create(
        user ? `put` : `post`,
        `/users/${user ? user?._id : ``}`
      )
        .call(
          user
            ? {
                isActive: isActive,
                roles: newRoles,
                permission: permission,
                sipPassword: sipPassword,
                sipExtension: sipExtension,
              }
            : {
                isActive: isActive,
                email: email,
                firstName: firstName,
                lastName: lastName,
                roles: newRoles,
                permission: permission,
                sipPassword: sipPassword,
                sipExtension: sipExtension,
              }
        )
        .exit((_res) => {
          mutateUsers(`/users/`);
        })
        .catch((err) => console.error(err))
        .finally(() => {
          setIsLoading(false);
          setSelectedUser(null);
          setDisplayContent('ElementList');
        });
    }
  };

  return (
    <Container sx={{ marginTop: 5 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-evenly',
          marginBottom: 4,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          <FormControlLabel
            sx={{ paddingRight: 2 }}
            control={
              <Checkbox checked={isActive} onChange={handleIsActiveChange} />
            }
            label="Actif"
            labelPlacement="start"
          />
          <FormControlLabel
            sx={{ margin: 1 }}
            control={
              <TextField
                label="Email"
                value={email}
                onChange={handleEmailChange}
                disabled={Boolean(user)}
                error={Boolean(emailError)}
                helperText={emailError}
              />
            }
          />
          <FormControlLabel
            sx={{ margin: 1 }}
            control={
              <TextField
                label="Prénom"
                value={firstName}
                onChange={handleFirstNameChange}
              />
            }
          />
          <FormControlLabel
            sx={{ margin: 1 }}
            control={
              <TextField
                label="Nom"
                value={lastName}
                onChange={handleLastNameChange}
              />
            }
          />
          <FormControlLabel
            sx={{ margin: 1 }}
            control={
              <TextField
                label="Mot de passe SIP"
                value={sipPassword}
                onChange={handleSipPasswordChange}
              />
            }
          />
          <FormControlLabel
            sx={{ margin: 1 }}
            control={
              <TextField
                label="Extension SIP"
                value={sipExtension}
                onChange={handleSipExtensionChange}
              />
            }
          />
          <Box>
            <Typography sx={{ fontSize: '12px', marginLeft: 1 }}>
              Niv de perm - Pourc max - Val max
            </Typography>
            <FormControlLabel
              sx={{ marginLeft: 1 }}
              control={
                <Select
                  value={permission}
                  onChange={handleChangePermission}
                  sx={{ width: 235 }}
                >
                  {permissions?.map((permission) => {
                    return (
                      <MenuItem key={permission?._id} value={permission?._id}>
                        Niveau {permission?.level} -{' '}
                        {permission?.maxPercentage === -1
                          ? '∞'
                          : permission?.maxPercentage}
                        % -{' '}
                        {permission?.maxValue === -1
                          ? '∞'
                          : permission?.maxValue}
                        €
                      </MenuItem>
                    );
                  })}
                </Select>
              }
            />
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'left',
          }}
        >
          {roles?.map((role) => {
            return (
              <FormControlLabel
                key={role?._id}
                sx={{ paddingRight: 2 }}
                control={
                  <Checkbox
                    checked={newRoles?.includes(role?._id) || false}
                    onChange={(_e) => handleRolesChange(role?._id)}
                  />
                }
                label={role?.name}
                labelPlacement="end"
              />
            );
          })}
        </Box>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-evenly' }}>
        <FormControlLabel
          onClick={handleCancelClick}
          control={
            <Button
              variant="outlined"
              color="error"
              startIcon={<PersonAddDisabledIcon />}
            >
              Annuler
            </Button>
          }
        />
        <LoadingButton
          onClick={handleConfirmClick}
          variant="outlined"
          color="success"
          endIcon={<PersonAddIcon />}
          isLoading={isLoading}
          disabled={
            email?.length === 0 ||
            firstName?.length === 0 ||
            lastName?.length === 0
          }
        >
          Confirmer
        </LoadingButton>
      </Box>
    </Container>
  );
};

export default ManageUser;
